import type { FC, SVGProps } from 'react';

import type { EntityLabel } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';

import { ReactComponent as AssetManagementIcon } from '../../assets/icons/Asset Management.svg';
import { ReactComponent as SustainabilityIcon } from '../../assets/icons/Sustainability.svg';
import { ReactComponent as SyntiroHoneyIcon } from '../../assets/icons/Metrics.svg';
import { ReactComponent as UtilityManagementIcon } from '../../assets/icons/Utility.svg';
import { ReactComponent as ExportsIcon } from '../../assets/icons/Download.svg';
import { ReactComponent as ConfigurationIcon } from '../../assets/icons/Configuration-09.svg';
import { ReactComponent as ContactIcon } from '../../assets/icons/Contact.svg';
import { ReactComponent as ResourceCentreIcon } from '../../assets/icons/Folder.svg';
import { ReactComponent as ForecastingRiskIcon } from '../../assets/icons/Forecasting and Risk.svg';
import { ReactComponent as TradingIcon } from '../../assets/icons/Trading.svg';
import { CUSTOM_DASHBOARD_NAMES_RAW, ENABLE_SYNTIRO_HONEY_MODULE } from '../../env';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';

const SOURCE = 'lib/app/navigation.ts';

export interface Module {
  id: string;
  name: string;
  description: string;
  icon: FC<SVGProps<SVGSVGElement>>;
  route: string;
  views: Array<View>;
  require_super_admin?: boolean;
}

export interface View {
  id: string;
  name: string;
  description: string;
  route: string;
  require_pbi_report: boolean; // Requires the company to have a pbi_report configured for the given view_id (e.g. energy_dashboard).
  require_nav_link: boolean; // Requires the company to have configured navigation_links (e.g. Tridium link in Firestore).
  allow_write_access?: boolean; // Controls whether a view supports write access - false limits to Read/None, true allows Read/Write/None (e.g. non-writable views like dashboards).
  allow_external_users?: boolean; // Controls whether external users can be assigned access to the view in User Management.
  hideWhenPbiMissing?: boolean; // When true, the view is hidden entirely from the side menu when no PBI report is configured for this company.
  disabled?: boolean; // When true, the view is always shown greyed out in the sidebar regardless of user permissions.
  hideWhenInaccessible?: boolean; // When true, the view is hidden entirely from the side menu when access is not granted.
}

export interface ViewExtended extends View {
  module: Module;
}

// Singular and plural label for modules.
export const module_label: EntityLabel = {
  one: 'Module',
  many: 'Modules'
};

// Singular and plural label for views.
export const view_label: EntityLabel = {
  one: 'View',
  many: 'Views'
};

export const modules: Array<Module> = [
  {
    id: 'forecasting_risk',
    name: 'Forecasting & Risk',
    description: 'Quantified energy risk intelligence and cost forecasting.',
    icon: ForecastingRiskIcon,
    route: 'forecasting_risk',
    require_super_admin: true,
    views: [
      {
        id: 'quantified_risk',
        name: 'Quantified Risk',
        description: 'Energy risk intelligence report with AI chat assistant.',
        route: 'quantified_risk',
        require_pbi_report: false,
        require_nav_link: false,
        allow_write_access: false,
        allow_external_users: false
      },
      {
        id: 'forecasting',
        name: 'Forecasting',
        description: 'Energy cost forecasting report with AI chat assistant.',
        route: 'forecasting',
        require_pbi_report: false,
        require_nav_link: false,
        allow_write_access: false,
        allow_external_users: false
      },
      {
        id: 'operational_cost_forecast',
        name: 'Operational Cost Forecast',
        description: 'Operational cost forecast (coming soon).',
        route: 'operational_cost_forecast',
        require_pbi_report: false,
        require_nav_link: false,
        allow_write_access: false,
        allow_external_users: false,
        disabled: true
      }
    ]
  },
  {
    id: 'trading',
    name: 'Trading',
    description: 'Trading Dashboards and Exports',
    icon: TradingIcon,
    route: 'trading',
    views: [
      {
        id: 'customer_dashboard',
        name: 'Customer Dashboard',
        description: 'Customer Dashboard',
        route: 'customer_dashboard',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'generator_dashboard',
        name: 'Generator Dashboard',
        description: 'Generator Dashboard',
        route: 'generator_dashboard',
        require_pbi_report: true,
        require_nav_link: false,
        hideWhenInaccessible: true
      },
      {
        id: 'generator_allocation_files',
        name: 'Generator Allocation Files',
        description: 'Generator Allocation Files',
        route: 'generator_allocation_files',
        require_pbi_report: false,
        require_nav_link: false,
        hideWhenInaccessible: true,
        disabled: true
      }
    ]
  },
  {
    id: 'sustainability',
    name: 'Sustainability',
    description: 'ESG Data Management and Reporting',
    icon: SustainabilityIcon,
    route: 'sustainability',
    views: [
      {
        id: 'readiness_gauge',
        name: 'Readiness Gauge',
        description: `Questionnaire detailing an enterprise's overall compliance with ESG goals.`,
        route: 'readiness',
        require_pbi_report: false,
        require_nav_link: false,
        allow_write_access: true
      },
      {
        id: 'metric_collection',
        name: 'Metric Collection',
        description: `Capture quantitative and qualitative data for Standards.`,
        route: 'metrics',
        require_pbi_report: false,
        require_nav_link: false,
        allow_write_access: true,
        allow_external_users: true
      },
      {
        id: 'carbon_dashboard',
        name: 'Carbon Dashboard',
        description: `Carbon Dashboard.`,
        route: 'carbon_dashboard',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'esg_dashboard',
        name: 'ESG Dashboard',
        description: `View ESG compliance analytics.`,
        route: 'esg_dashboard',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'data_import',
        name: 'Data Import',
        description: `Import data into Syntiro from CSV and other sources.`,
        route: 'import',
        require_pbi_report: false,
        require_nav_link: false,
        allow_write_access: true,
        allow_external_users: true
      }
    ]
  },
  ...(ENABLE_SYNTIRO_HONEY_MODULE
    ? [
        {
          id: 'syntiro_honey',
          name: 'ESG Data Ingestion',
          description: 'AI Facilitated ESG Data Ingestion',
          icon: SyntiroHoneyIcon,
          route: 'syntiro-honey',
          views: [
            {
              id: 'data_ingestion',
              name: 'Data Ingestion',
              description: `AI Facilitated ESG Data Ingestion.`,
              route: 'data-ingestion',
              require_pbi_report: false,
              require_nav_link: false,
              allow_write_access: true
            }
          ]
        }
      ]
    : []),
  {
    id: 'utility_management',
    name: 'Utility Management',
    description: 'Energy Operations Analysis and Management',
    icon: UtilityManagementIcon,
    route: 'utility_management',
    views: [
      {
        id: 'strategic_dashboard',
        name: 'Strategic Dashboard',
        description: `Track current performance and identify strategic opportunities.`,
        route: 'strategic_dashboard',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'energy_dashboard',
        name: 'Energy Dashboard',
        description: `View energy consumption, costs and baseline analytics.`,
        route: 'energy_dashboard',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'water_dashboard',
        name: 'Water Dashboard',
        description: `View water consumption, costs and baseline analytics.`,
        route: 'water_dashboard',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'gas_dashboard',
        name: 'Gas Dashboard',
        description: `View gas consumption, costs and baseline analytics.`,
        route: 'gas_dashboard',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'operational_dashboard',
        name: 'Operational Dashboard',
        description: `Operational insights including waste and efficiency.`,
        route: 'operational_dashboard',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'tenant_dashboard',
        name: 'Tenant Dashboard',
        description: `View tenant consumption, costs and recoveries.`,
        route: 'tenant_dashboard',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'custom_dashboard',
        name: 'Custom Dashboard',
        description: 'Custom Power BI dashboard tailored to your company.',
        route: 'custom_dashboard',
        require_pbi_report: true,
        require_nav_link: false,
        hideWhenPbiMissing: true
      }
    ]
  },
  {
    id: 'asset_management',
    name: 'Asset Management',
    description: 'Asset and Plant Monitoring + Control Systems',
    icon: AssetManagementIcon,
    route: 'asset_management',
    views: [
      {
        id: 'air_handling_units',
        name: 'Air Handling Units',
        description: `Asset Class Dashboard for Air Handling Units.`,
        route: 'air_handling_units',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'autoclaves',
        name: 'Autoclaves',
        description: 'Asset Class Dashboard for Autoclaves',
        route: 'autoclaves',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'bess',
        name: 'BESS',
        description: `Asset Class Dashboard for BESS.`,
        route: 'bess',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'borehole_and_ro_plants',
        name: 'Borehole and RO Plants',
        description: `Asset Class Dashboard for Borehole and RO Plants.`,
        route: 'borehole_and_ro_plants',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'bulk_water_tanks',
        name: 'Bulk Water Tanks',
        description: `Asset Class Dashboard for Bulk Water Tanks.`,
        route: 'bulk_water_tanks',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'chilled_water_plants',
        name: 'Chilled Water Plants',
        description: `Asset Class Dashboard for Chilled Water Plants.`,
        route: 'chilled_water_plants',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'compressed_air_systems',
        name: 'Compressed Air Systems',
        description: `Asset Class Dashboard for Compressed Air Systems.`,
        route: 'compressed_air_systems',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'data_quality',
        name: 'Data Quality',
        description: `Dashboard for meter Data Quality.`,
        route: 'data_quality',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'diesel_generators',
        name: 'Diesel Generators',
        description: `Asset Class Dashboard for Diesel Generators.`,
        route: 'diesel_generators',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'fresh_air_pretreatment',
        name: 'Fresh Air Pretreatment',
        description: `Asset Class Dashboard for Fresh Air Pretreatment.`,
        route: 'fresh_air_pretreatment',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'gas_storage_systems',
        name: 'Gas Storage Systems',
        description: `Asset Class Dashboard for Gas Storage Systems.`,
        route: 'gas_storage_systems',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'general_facility',
        name: 'General Facility',
        description: `Asset Class Dashboard for General Facility.`,
        route: 'general_facility',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'hot_water_plants',
        name: 'Hot Water Plants',
        description: `Asset Class Dashboard for Hot Water Plants.`,
        route: 'hot_water_plants',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'lighting_systems',
        name: 'Lighting Systems',
        description: `Asset Class Dashboard for Lighting Systems.`,
        route: 'lighting_systems',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'management_overview',
        name: 'Management Overview',
        description: `Dashboard for the general Management Overview.`,
        route: 'management_overview',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'oxygen',
        name: 'Oxygen',
        description: `Asset Class Dashboard for Oxygen.`,
        route: 'oxygen',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'refrigeration_plants_caas',
        name: 'Refrigeration Plants (CaaS)',
        description: `Asset Class Dashboard for Refrigeration Plants (CaaS).`,
        route: 'refrigeration_plants_caas',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'refrigeration_plants',
        name: 'Refrigeration Plants',
        description: `Asset Class Dashboard for Refrigeration Plants (non-CaaS).`,
        route: 'refrigeration_plants',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'solar_pv',
        name: 'Solar PV',
        description: `Asset Class Dashboard for Solar PV.`,
        route: 'solar_pv',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'steam_generation_plants',
        name: 'Steam Generation Plants',
        description: `Asset Class Dashboard for Steam Generation Plants.`,
        route: 'steam_generation_plants',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'vacuum_pumps',
        name: 'Vacuum Pumps',
        description: `Asset Class Dashboard for Vacuum Pumps.`,
        route: 'vacuum_pumps',
        require_pbi_report: true,
        require_nav_link: false
      },
      {
        id: 'syntiroLive',
        name: 'Syntiro Live',
        description: `See real time performance of your operational assets.`,
        route: 'syntiro-live',
        require_pbi_report: false,
        require_nav_link: true
      }
    ]
  },
  {
    id: 'resource_centre',
    name: 'Resource Centre',
    description: `Browse Syntiro's document library.`,
    icon: ResourceCentreIcon,
    route: 'resources',
    views: [
      {
        id: 'explore_resources',
        name: 'Explore Resources',
        description: `Explore available resources.`,
        route: 'explore',
        require_pbi_report: false,
        require_nav_link: false,
        allow_write_access: false,
        allow_external_users: false
      }
    ]
  },
  {
    id: 'exports',
    name: 'Exports',
    description: 'Export your data from all Syntiro modules',
    icon: ExportsIcon,
    route: 'exports',
    views: [
      {
        id: 'export_data',
        name: 'Export Data',
        description: `Export data for external systems.`,
        route: 'manage',
        require_pbi_report: false,
        require_nav_link: false,
        allow_write_access: false,
        allow_external_users: false
      }
    ]
  },
  {
    id: 'configuration',
    name: 'Configuration',
    description: 'Configure your Company on Syntiro',
    icon: ConfigurationIcon,
    route: 'configuration',
    views: []
  },
  {
    id: 'contact_us',
    name: 'Contact Us',
    description: 'Get in touch with EP Sustainability',
    icon: ContactIcon,
    route: 'contact',
    views: []
  }
];

/**
 * Get a list of views across one or all modules.
 * @param {string | undefined} module_id Optionally only get views for specific modules.
 * @returns {Array<ViewExtended>}
 */
export const getViews = (module_id?: string): Array<ViewExtended> => {
  return modules
    .filter((module: Module) => (module_id ? module.id === module_id : true))
    .map((module: Module) => module.views.map((view: View) => ({ ...view, module: module })))
    .flat();
};

/**
 * Gets the module ID for a given view.
 * @param {string} view_id The ID of the view to look up.
 * @returns {string}
 */
export const getModuleId = (view_id: string): string => {
  const all_views: Array<ViewExtended> = getViews();
  const view: ViewExtended | undefined = all_views.find((view) => {
    return view.id === view_id;
  });
  return view?.module.id ?? '';
};

/**
 * Resolves the view a pathname lands on, matching its last non-empty segment against each view's
 * `route` — the segment the router itself keys off. Matched on `route` rather than `id` because the
 * two diverge for some views (`readiness_gauge` is served at `/sustainability/readiness/`).
 * @param {string} pathname A location pathname, e.g. `/sustainability/readiness/`.
 * @returns {ViewExtended | undefined}
 */
export function getViewByPathname(pathname: string): ViewExtended | undefined {
  const lastSegment: string | undefined = pathname
    .split('/')
    .filter((segment: string) => segment.length > 0)
    .pop();
  if (!lastSegment) return undefined;
  return getViews().find((view: ViewExtended) => view.route === lastSegment);
}

/** The view ID used to identify the custom dashboard. */
export const CUSTOM_DASHBOARD_VIEW_ID = 'custom_dashboard';

/**
 * Parses the REACT_APP_CUSTOM_DASHBOARD_NAMES env var JSON into a company-id-to-name map.
 * Returns an empty object on malformed or missing input so the app never crashes.
 * @param {string} rawNames Raw JSON string from the environment variable.
 * @returns {Record<string, string>}
 */
export function parseCustomDashboardNames(rawNames: string): Record<string, string> {
  if (!rawNames) return {};
  try {
    return JSON.parse(rawNames) as Record<string, string>;
  } catch (err: unknown) {
    const trackingId: string = generateUuidV4();
    log(
      'error',
      new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'parseCustomDashboardNames'),
        { rawNames },
        trackingId
      )
    );
    return {};
  }
}

const customDashboardNames = Object.freeze(
  parseCustomDashboardNames(CUSTOM_DASHBOARD_NAMES_RAW ?? '')
);

/**
 * Returns true when the given company has a custom dashboard configured in the env name map.
 * @param {string | null | undefined} companyId The ID of the company to check.
 * @returns {boolean}
 */
export function hasCustomDashboard(companyId: string | null | undefined): boolean {
  if (!companyId) return false;
  return Object.hasOwn(customDashboardNames, companyId);
}

/**
 * Returns the custom dashboard display name for the given company, or undefined if not configured.
 * @param {string | null | undefined} companyId The ID of the current company.
 * @returns {string | undefined}
 */
export function getCustomDashboardName(companyId: string | null | undefined): string | undefined {
  if (!companyId) return undefined;
  return customDashboardNames[companyId];
}

/**
 * Returns the resolved display name for a view.
 * For the custom dashboard, applies the per-company name override; falls back to the static view
 * name when no override is configured. Use {@link hasCustomDashboard} to gate visibility separately.
 * All other views always return their static name.
 * @param {View} view The view whose display name should be resolved.
 * @param {string | null | undefined} companyId The ID of the current company.
 * @returns {string}
 */
export function resolveViewName(view: View, companyId: string | null | undefined): string {
  if (view.id !== CUSTOM_DASHBOARD_VIEW_ID) return view.name;
  return getCustomDashboardName(companyId) ?? view.name;
}
